import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import SiteHeader from '../components/SiteHeader';
import RequireSession from '../components/RequireSession';
import {
  apiErrorMessage,
  fetchPayoutDefault,
  isPayoutChangeRequested,
  updateDefaultPayoutAddress,
  type PayoutDefaultChangeRequested,
  type PayoutDefaultUpdate,
} from '../lib/api';
import { usePayoutAddressValidation, KIND_NAMES } from '../hooks/usePayoutAddressValidation';

/**
 * My Account.
 *
 * ===========================================================================
 * ONE SECTION, BECAUSE THERE IS ONE THING TO MANAGE.
 *
 * The identity model is "proved control of an email address": there is no password, no
 * profile and no preferences. The payout wallet is the single piece of account state a
 * customer genuinely owns, so it is the whole page rather than one card among stubs.
 *
 * ===========================================================================
 * THIS IS THE DEFAULT, NOT AN ORDER.
 *
 * Saving here changes what NEW orders use. The checkbox additionally asks us to move
 * rentals that are already running. The order page changes exactly one order and never
 * touches this value. Keeping the two apart is what lets a customer correct a single
 * rental without silently rewriting where everything else pays.
 *
 * ===========================================================================
 * THE DEFAULT SAVES EVEN WHEN A RENTAL WILL NOT MOVE.
 *
 * The server saves the wallet first, then attempts each live rental independently. So a
 * rental that could not be moved is NOT a failed save, and this page must never say it
 * was. It reports both facts separately, using the server's counts.
 */

/**
 * What the save produced, in the customer's terms.
 *
 * `requested` IS NOT `saved`. Ticking the checkbox asks us to redirect rentals that are
 * currently mining, which takes an emailed confirmation -- so that request saves nothing
 * yet, and this page must not report a save it did not get. The server distinguishes the
 * two outcomes; so does this.
 */
type SaveResult =
  | { status: 'idle' }
  | { status: 'saving' }
  | { status: 'saved'; result: PayoutDefaultUpdate }
  | { status: 'requested'; result: PayoutDefaultChangeRequested };

/**
 * The outcome, in one sentence plus a caveat.
 *
 * ZERO ELIGIBLE ORDERS IS NOT A FAILURE AND NOT A STATISTIC. A customer with nothing
 * running who saves a wallet should read "Payout wallet saved." and nothing else --
 * "0 of 0 active orders were updated" is technically true and reads like a malfunction.
 */
function describeSave(result: PayoutDefaultUpdate): { headline: string; caveat: string | null } {
  const saved = 'Payout wallet saved.';

  if (!result.applied || result.eligibleCount === 0) {
    return { headline: saved, caveat: null };
  }

  if (result.failedCount === 0) {
    const plural = result.updatedCount === 1 ? 'order was' : 'orders were';
    return {
      headline: `${saved} ${result.updatedCount} active ${plural} updated.`,
      caveat: null,
    };
  }

  const plural = result.failedCount === 1 ? 'order still uses' : 'orders still use';
  return {
    headline: `${saved} ${result.updatedCount} of ${result.eligibleCount} active orders were updated.`,
    caveat: `${result.failedCount} ${plural} its previous payout address.`,
  };
}

function PayoutWalletSection() {
  const [address, setAddress] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  /*
   * WHAT THE SERVER ACTUALLY HOLDS, kept apart from the editable field.
   *
   * The field is PREFILLED with it, so on arrival the two are equal and there is nothing
   * to save. Comparing against this -- rather than against the previous input value --
   * is what makes "typed something else, then typed it back" correctly inert again.
   *
   * Null means no default has ever been saved, in which case any valid address is a
   * genuine change.
   */
  const [savedAddress, setSavedAddress] = useState<string | null>(null);

  /*
   * ON BY DEFAULT. A customer changing where they are paid almost always means "from now
   * on", including the rentals currently running. The alternative default quietly leaves
   * live rentals paying an old wallet, which is the outcome they came here to prevent.
   */
  const [applyToActiveOrders, setApplyToActiveOrders] = useState(true);

  const [save, setSave] = useState<SaveResult>({ status: 'idle' });
  const [saveError, setSaveError] = useState<string | null>(null);

  // The same authoritative verdict the configurator and the order page use.
  const check = usePayoutAddressValidation(address);

  /*
   * NOTHING TO SAVE, so nothing is sent.
   *
   * Saving the address already on file used to issue a confirmation token and send an
   * email asking the customer to approve a change to the wallet they are already using.
   *
   * DELIBERATELY INDEPENDENT OF THE CHECKBOX. "Also update my active mining orders" does
   * not change the address, so toggling it cannot make an unchanged address saveable.
   *
   * Compared exactly, not case-insensitively: a case variant is treated as a change,
   * which at worst permits a pointless save rather than blocking a real one.
   */
  const isUnchanged = savedAddress !== null && address.trim() === savedAddress;

  useEffect(() => {
    const controller = new AbortController();

    fetchPayoutDefault(controller.signal)
      .then((current) => {
        // The saved wallet, in full, so it can be read back and edited. Empty when there
        // is none: nothing is inferred from order history.
        if (current.payoutAddress) setAddress(current.payoutAddress);
        setSavedAddress(current.payoutAddress ?? null);
        setLoaded(true);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setLoadError(apiErrorMessage(caught, 'We could not load your payout wallet.'));
        setLoaded(true);
      });

    return () => controller.abort();
  }, []);

  async function submit(event: React.FormEvent) {
    event.preventDefault();

    /*
     * NOTHING UNVALIDATED OR UNCHANGED REACHES THE PATCH. Guarded here as well as on the
     * button, because a form submits on Enter and a click can land while a check is in
     * flight. `save.status === 'saving'` is what stops a double submission.
     */
    if (save.status === 'saving' || !check.isSubmittable || isUnchanged) return;

    setSave({ status: 'saving' });
    setSaveError(null);

    try {
      const submitted = address.trim();
      const result = await updateDefaultPayoutAddress(submitted, applyToActiveOrders);

      // Narrowed, never assumed. The confirmation shape carries no counts at all.
      setSave(
        isPayoutChangeRequested(result)
          ? { status: 'requested', result }
          : { status: 'saved', result },
      );

      /*
       * ONLY AN IMMEDIATE SAVE MOVES THE PERSISTED VALUE.
       *
       * A confirmation request saves nothing until the emailed link is claimed, so
       * `savedAddress` must NOT move for it -- otherwise the button would disable itself
       * and the page would imply the change had landed.
       *
       * Taken from what we submitted, not from the response: the response address is
       * masked, and the submitted one is what was actually persisted.
       */
      if (!isPayoutChangeRequested(result)) setSavedAddress(submitted);
    } catch (caught: unknown) {
      // The server's own safe message. It never carries provider detail.
      setSaveError(apiErrorMessage(caught, 'We could not save your payout wallet.'));
      setSave({ status: 'idle' });
    }
  }

  if (!loaded) {
    return (
      <p className="mm-help" role="status">
        Loading your payout wallet…
      </p>
    );
  }

  const summary = save.status === 'saved' ? describeSave(save.result) : null;
  const failedOrders =
    save.status === 'saved' ? save.result.orders.filter((order) => order.outcome === 'failed') : [];

  return (
    <section aria-labelledby="payout-wallet-heading" className="mm-panel p-5 sm:p-6">
      <h2 id="payout-wallet-heading" className="mm-section-title">
        Payout wallet
      </h2>
      <p className="mm-help mt-2">
        This address is used as the default for new mining orders.
      </p>

      {loadError && (
        <p role="alert" className="mm-note mm-note--danger mt-4">
          <span>{loadError}</span>
        </p>
      )}

      <form onSubmit={(event) => void submit(event)} className="mt-5" data-testid="payout-wallet-form">
        <label htmlFor="account-payout-address" className="mm-label text-sm">
          BTC payout address
        </label>

        <input
          id="account-payout-address"
          type="text"
          value={address}
          onChange={(event) => {
            setAddress(event.target.value);
            // A new address makes the previous outcome stale, so it stops being reported.
            if (save.status === 'saved') setSave({ status: 'idle' });
            setSaveError(null);
          }}
          autoComplete="off"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          disabled={save.status === 'saving'}
          data-testid="account-payout-input"
          aria-invalid={check.state.status === 'invalid'}
          data-state={
            check.state.status === 'invalid'
              ? 'invalid'
              : check.state.status === 'valid'
                ? 'valid'
                : undefined
          }
          placeholder="bc1…"
          // 16px on mobile: iOS zooms a focused input under that size.
          className="mm-input mt-2 font-mono text-base sm:text-sm"
        />

        {/* Reserved height, so the card does not resize as messages come and go. */}
        <p
          role="status"
          aria-live="polite"
          data-testid="account-payout-validation"
          className="mt-1.5 min-h-[1.25rem] text-sm"
          style={{
            color:
              check.state.status === 'invalid'
                ? 'var(--mm-danger)'
                : check.state.status === 'valid'
                  ? 'var(--mm-success)'
                  : 'var(--mm-text-subtle)',
          }}
        >
          {check.state.status === 'checking' && 'Checking address…'}
          {check.state.status === 'valid' && (
            <>
              <span aria-hidden="true">✓ </span>
              Valid {KIND_NAMES[check.state.kind] ?? check.state.kind} address.
            </>
          )}
          {check.state.status === 'invalid' && check.state.reason}
        </p>

        <label className="mt-4 flex items-start gap-2.5 text-sm">
          <input
            type="checkbox"
            checked={applyToActiveOrders}
            onChange={(event) => setApplyToActiveOrders(event.target.checked)}
            disabled={save.status === 'saving'}
            data-testid="apply-to-active"
            className="mt-0.5"
          />
          <span>
            Also update my active mining orders
            <span className="mm-help mt-0.5 block">
              Active rentals will be updated to use this wallet.
            </span>
          </span>
        </label>

        {saveError && (
          <p role="alert" className="mm-note mm-note--danger mt-4" data-testid="account-payout-error">
            <span>{saveError}</span>
          </p>
        )}

        <div className="mt-5">
          <button
            type="submit"
            disabled={save.status === 'saving' || !check.isSubmittable || isUnchanged}
            data-testid="save-payout-wallet"
            className="mm-btn-primary inline-flex items-center justify-center sm:w-auto"
          >
            {save.status === 'saving' ? 'Saving…' : 'Save payout wallet'}
          </button>
        </div>
      </form>

      {/*
        WHAT ACTUALLY HAPPENED, which is not "saved".

        Nothing has been written and no rental has moved: the server recorded the request
        and emailed a single-use link. Saying "Payout wallet saved." here would tell the
        customer their mining rewards had been redirected when they had not -- precisely
        the lie the confirmation step exists to prevent.
      */}
      {save.status === 'requested' && (
        <div className="mt-5" data-testid="account-payout-confirmation-sent">
          <p role="status" className="mm-note">
            <span>
              {save.result.emailSent
                ? 'Check your email and confirm the change. Your payout wallet has not changed yet.'
                : 'We could not send the confirmation email just now. Your payout wallet has not changed. Please try again.'}
            </span>
          </p>
        </div>
      )}

      {summary && (
        <div className="mt-5" data-testid="account-payout-saved">
          <p role="status" className="mm-note mm-note--success">
            <span>{summary.headline}</span>
          </p>

          {summary.caveat && (
            <>
              <p className="mm-help mt-3" data-testid="account-payout-caveat">
                {summary.caveat}
              </p>

              {/*
                ACTIONABLE, because the customer's next question is "which one". Their own
                order numbers, linking to the page where that one rental can be retried.
              */}
              {failedOrders.length > 0 && (
                <ul className="mt-2 flex list-none flex-wrap gap-x-4 gap-y-1 p-0">
                  {failedOrders.map((order) => (
                    <li key={order.orderNumber}>
                      <Link
                        to={`/orders/${order.orderNumber}`}
                        data-testid={`failed-${order.orderNumber}`}
                        className="mm-numeric text-sm underline underline-offset-2"
                      >
                        {order.orderNumber}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}

export default function Account() {
  return (
    <div className="flex min-h-dvh flex-col">
      <SiteHeader />
      <main className="mx-auto w-full max-w-2xl px-5 py-10 sm:px-8 sm:py-14 flex-1 min-w-0">
        <h1 className="mm-display">Your account</h1>
        <p className="mm-lede mt-4">Manage where your mining rewards are paid.</p>

        <div className="mt-8">
          <RequireSession>
            <PayoutWalletSection />
          </RequireSession>
        </div>
      </main>
    </div>
  );
}
